import React, { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MessageSquare, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { SystemDocsPanel } from "@/components/help/SystemDocsPanel";
import { HelpArticleList, MetaFields } from "@/components/help/HelpArticleList";
import { BodyEditor } from "@/components/help/BodyEditor";
import { HelpCategoriesPanel } from "@/components/help/HelpCategoriesPanel";
import { SharedTopicsPanel } from "@/components/knowledge/SharedTopicsPanel";

export default function HelpManager() {
  const { isSuper, isAdmin, isGroupAdmin, ctx, activeAccount } = useAuth();
  const adminGroups = [...new Map((ctx?.memberships || []).filter((m) => m.client_account_id === activeAccount && m.role === "group_admin" && m.organization_id)
    .map((m) => [m.organization_id, { id: m.organization_id, name: m.organization_name || "Group" }])).values()];
  const defaultScope = isSuper ? "system" : isAdmin ? "account" : "group";
  const [superOrgs, setSuperOrgs] = useState([]);
  useEffect(() => {
    if (isSuper) { api.get("/organizations").then(({ data: d }) => setSuperOrgs(d.organizations || d || [])).catch(() => setSuperOrgs([])); }
  }, [isSuper, activeAccount]);
  const scopeVal = (e) => (e.scope_type === "group" ? `group:${e.group_id || ""}` : e.scope_type);
  const setScopeVal = (v) => setEdit((e) => (v.startsWith("group:") ? { ...e, scope_type: "group", group_id: v.slice(6) } : { ...e, scope_type: v, group_id: null }));
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [audience, setAudience] = useState({ roles: [], labels: {} });
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("articles");
  const [feedback, setFeedback] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [requests, setRequests] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    api.get("/help/admin/articles").then((r) => { setData(r.data.items); setCats(r.data.categories || []); setAudience({ roles: r.data.audience_roles || [], labels: r.data.role_labels || {} });
      const lab = (l) => Object.fromEntries((l || []).map((x) => [x.key, x.label]));
      setMeta({ categories: r.data.categories || [], audiences: r.data.audiences || [], programs: r.data.programs || [],
        catLabel: lab(r.data.categories), audLabel: lab(r.data.audiences), progLabel: lab(r.data.programs) }); }).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "feedback") api.get("/help/admin/feedback").then((r) => setFeedback(r.data.items)).catch(() => {});
    if (tab === "analytics") api.get("/help/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    if (tab === "requests") api.get("/help/support/requests", { params: { status: "all" } }).then((r) => setRequests(r.data.items)).catch(() => {});
  }, [tab]);

  const openNew = () => setEdit({ title: "", summary: "", body: "", category: "getting-started", status: "draft", allowed_roles: [], featured: false, order: 100, scope_type: defaultScope, group_id: defaultScope === "group" && adminGroups.length === 1 ? adminGroups[0].id : "" });
  const openEdit = async (row) => { try { const { data } = await api.get(`/help/admin/articles/${row.id}`); setEdit({ ...data, category: cats.some((c) => c.key === data.category) ? data.category : row.category }); } catch (e) { toast.error(apiError(e)); } };
  const save = async () => {
    if (edit.scope_type === "group" && !edit.id && !edit.group_id) { toast.error("Choose the Group for this article."); return; }
    const { search_text, duplicates, raw_category, views, ...body } = edit;
    try {
      if (edit.id) await api.put(`/help/admin/articles/${edit.id}`, body);
      else await api.post("/help/admin/articles", body);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this article?")) return; try { await api.delete(`/help/admin/articles/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggleRole = (r) => setEdit((e) => ({ ...e, allowed_roles: e.allowed_roles.includes(r) ? e.allowed_roles.filter((x) => x !== r) : [...e.allowed_roles, r] }));

  return (
    <div className="space-y-5" data-testid="help-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><HelpCircle className="w-5 h-5 text-brand" />Help Center Content</h1>
          <p className="text-sm text-slate-500">Create and manage help articles, FAQs and glossary terms.</p>
        </div>
        <Button onClick={openNew} className="bg-brand hover:bg-brand-strong text-white" data-testid="help-new-article"><Plus className="w-4 h-4 mr-1.5" />New Article</Button>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button onClick={() => setTab("articles")} data-testid="help-tab-articles" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "articles" ? "bg-white shadow-sm" : "text-slate-500"}`}>Articles</button>
        <button onClick={() => setTab("categories")} data-testid="help-tab-categories" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "categories" ? "bg-white shadow-sm" : "text-slate-500"}`}>Help Categories</button>
        <button onClick={() => setTab("topics")} data-testid="help-tab-topics" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "topics" ? "bg-white shadow-sm" : "text-slate-500"}`}>Shared Topics</button>
        <button onClick={() => setTab("feedback")} data-testid="help-tab-feedback" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "feedback" ? "bg-white shadow-sm" : "text-slate-500"}`}>Feedback</button>
        <button onClick={() => setTab("analytics")} data-testid="help-tab-analytics" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "analytics" ? "bg-white shadow-sm" : "text-slate-500"}`}>Analytics</button>
        <button onClick={() => setTab("requests")} data-testid="help-tab-requests" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "requests" ? "bg-white shadow-sm" : "text-slate-500"}`}>Support Requests</button>
        <button onClick={() => setTab("system")} data-testid="help-tab-system" className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === "system" ? "bg-white shadow-sm" : "text-slate-500"}`}>System Docs & Gaps</button>
      </div>

      {tab === "system" ? <SystemDocsPanel /> : tab === "topics" ? <SharedTopicsPanel /> : tab === "categories" ? <HelpCategoriesPanel onChanged={load} /> : tab === "articles" ? (
        !data || !meta ? <p className="text-sm text-slate-400">Loading…</p> : (
          <HelpArticleList items={data} meta={meta} onEdit={openEdit} onDelete={del} reload={load} isSuper={isSuper} />
        )
      ) : tab === "feedback" ? (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="help-feedback-list">
          {feedback.length === 0 ? <p className="p-6 text-center text-sm text-slate-400">No feedback yet.</p> : feedback.map((f) => (
            <div key={f.id} className="p-3 flex items-start gap-3">
              <MessageSquare className={`w-4 h-4 mt-0.5 ${f.helpful ? "text-emerald-500" : "text-rose-500"}`} />
              <div><p className="text-sm text-slate-700">{f.title} — <span className={f.helpful ? "text-emerald-600" : "text-rose-600"}>{f.helpful ? "Helpful" : "Not helpful"}</span></p>{f.note && <p className="text-xs text-slate-500 mt-0.5">"{f.note}"</p>}</div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "analytics" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="help-analytics">
          {!analytics ? <p className="text-sm text-slate-400">Loading…</p> : <>
            {[["Article views", analytics.total_views], ["Searches", analytics.searches_total], ["Zero-result", analytics.zero_result_total], ["Support requests", analytics.support_requests]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-2xl font-bold text-slate-900">{v}</p><p className="text-xs text-slate-500">{l}</p></div>
            ))}
            <div className="rounded-xl border border-slate-200 bg-white p-4 col-span-2 sm:col-span-4">
              <p className="text-sm font-semibold mb-2">Most viewed articles</p>
              {analytics.top_viewed.filter((t) => t.views > 0).map((t) => <div key={t.slug} className="flex justify-between text-sm py-0.5"><span className="text-slate-600 truncate">{t.title}</span><span className="text-slate-400">{t.views}</span></div>)}
              <p className="text-xs text-slate-500 mt-2">Feedback: {analytics.feedback.helpful} helpful · {analytics.feedback.not_helpful} not helpful</p>
            </div>
            {analytics.top_zero_result?.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 col-span-2 sm:col-span-4" data-testid="help-zero-results">
                <p className="text-sm font-semibold mb-1">Searches with no results (documentation gaps)</p>
                {analytics.top_zero_result.map((z) => <span key={z.q} className="text-xs text-slate-600 mr-3">{z.q} ×{z.count}</span>)}
              </div>
            )}
          </>}
        </div>
      )}

      {tab === "requests" && (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="help-requests-list">
          {requests.length === 0 ? <p className="p-6 text-center text-sm text-slate-400">No support requests yet.</p> : requests.map((r) => (
            <div key={r.id} className="p-3" data-testid={`help-request-${r.id}`}>
              <p className="text-sm font-medium text-slate-800">{r.subject} <span className="text-xs text-slate-400">· {r.category} · {r.status}</span></p>
              <p className="text-xs text-slate-500 mt-0.5">{r.user_name} ({(r.roles || []).join(", ")}){r.context?.article_slug ? ` · from article ${r.context.article_slug}` : ""}{r.context?.page ? ` · ${r.context.page}` : ""}</p>
              {r.description && <p className="text-xs text-slate-600 mt-1">{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-auto" data-testid="help-edit-dialog">
            <DialogHeader><DialogTitle>{edit.id ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="help-edit-title" /></div>
              <div><Label className="text-xs">Summary</Label><Input value={edit.summary} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} data-testid="help-edit-summary" /></div>
              <BodyEditor edit={edit} setEdit={setEdit} />
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Category</Label>
                  <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                    <SelectTrigger data-testid="help-edit-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Status</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger data-testid="help-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="needs_review">Needs Review</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {isSuper && (
                <div>
                  <Label className="text-xs">Group</Label>
                  <select value={scopeVal(edit)} onChange={(e) => setScopeVal(e.target.value)} data-testid="help-scope-select"
                    className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm">
                    <option value="system">ALL Groups</option>
                    <option value="account">Entire Organization</option>
                    {superOrgs.map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name}</option>)}
                  </select>
                </div>)}
              {!isSuper && edit.scope_type === "group" && !edit.id && (
                <div>
                  <Label className="text-xs">Group</Label>
                  <select value={edit.group_id || ""} onChange={(e) => setEdit({ ...edit, group_id: e.target.value })} data-testid="help-group-select"
                    className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm">
                    <option value="">Choose Group</option>
                    {adminGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>)}
              <div>
                <Label className="text-xs">Visible to roles (none = everyone)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {[...audience.roles.map((a) => a.code), ...(edit.allowed_roles || []).filter((r) => !audience.roles.some((a) => a.code === r))].map((r) => (
                    <button key={r} type="button" onClick={() => toggleRole(r)} data-testid={`help-role-${r}`} title={r}
                      className={`text-xs px-2 py-1 rounded-full border ${edit.allowed_roles.includes(r) ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}>{audience.labels[r] || r}</button>))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} data-testid="help-edit-featured" />Featured</label>
              {meta && <MetaFields edit={edit} setEdit={setEdit} meta={meta} items={data || []} />}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
              <Button onClick={save} className="bg-brand hover:bg-brand-strong text-white" data-testid="help-edit-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
